import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  XMarkIcon, 
  CurrencyEuroIcon, 
  CalendarIcon, 
  PlusCircleIcon, 
  PhotoIcon, 
  FolderIcon, 
  ArrowPathIcon,
  TruckIcon,
  ReceiptPercentIcon,
  PencilSquareIcon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import roLocale from 'date-fns/locale/ro'; // Optional: for Romanian locale
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { FormControl, Select, MenuItem } from '@mui/material';
// Custom fuel icon since Heroicons doesn't have one

const ExpensesModal = ({ isOpen, onClose, selectedCar }) => {
  // Store original body styles and scroll position
  const bodyStyles = useRef({});

  // Tab state for mobile view
  const [activeTab, setActiveTab] = useState('add'); // 'add' or 'history'
  
  // Form state
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('fuel');
  const [date, setDate] = useState(new Date()); // Store as a Date object
  const [receiptImage, setReceiptImage] = useState(null);

  // Create a robust scroll lock mechanism
  useEffect(() => {
    if (isOpen) {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');
      
      // Store current scroll position and styles
      bodyStyles.current = {
        overflow: document.body.style.overflow,
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
        mainOverflow: mainElement?.style.overflow,
      };

      // Apply scroll lock styles
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.documentElement.style.overflow = 'hidden';
      
      // Also lock the main scrollable element
      if (mainElement) {
        mainElement.style.overflow = 'hidden';
      }
    } else if (bodyStyles.current.scrollY !== undefined) {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');
      
      // Restore original styles
      document.body.style.overflow = bodyStyles.current.overflow || '';
      document.body.style.position = bodyStyles.current.position || '';
      document.body.style.top = bodyStyles.current.top || '';
      document.body.style.width = bodyStyles.current.width || '';
      document.documentElement.style.overflow = '';
      
      // Restore main element overflow
      if (mainElement && bodyStyles.current.mainOverflow !== undefined) {
        mainElement.style.overflow = bodyStyles.current.mainOverflow || '';
      }
      
      // Restore scroll position
      if (mainElement) {
        mainElement.scrollTop = bodyStyles.current.scrollY;
      } else {
        window.scrollTo(0, bodyStyles.current.scrollY);
      }
    }

    return () => {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');
      
      // Always restore on unmount
      document.body.style.overflow = bodyStyles.current.overflow || '';
      document.body.style.position = bodyStyles.current.position || '';
      document.body.style.top = bodyStyles.current.top || '';
      document.body.style.width = bodyStyles.current.width || '';
      document.documentElement.style.overflow = '';
      
      // Restore main element
      if (mainElement && bodyStyles.current.mainOverflow !== undefined) {
        mainElement.style.overflow = bodyStyles.current.mainOverflow || '';
      }
      
      if (bodyStyles.current.scrollY !== undefined) {
        if (mainElement) {
          mainElement.scrollTop = bodyStyles.current.scrollY;
        } else {
          window.scrollTo(0, bodyStyles.current.scrollY);
        }
      }
    };
  }, [isOpen]);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  // Expenses list - in a real app this would come from API
  const [expenses, setExpenses] = useState([
    {
      id: 1,
      date: '15.05.2023',
      category: 'fuel',
      description: 'Plinul la benzinărie OMV',
      amount: '350 RON',
      hasReceipt: true
    },
    {
      id: 2,
      date: '10.05.2023',
      category: 'service',
      description: 'Schimb filtru aer condiționat',
      amount: '120 RON',
      hasReceipt: false
    },
    {
      id: 3,
      date: '02.05.2023',
      category: 'tax',
      description: 'Rovinieta 1 lună',
      amount: '50 RON',
      hasReceipt: true
    },
    {
      id: 4,
      date: '20.04.2023',
      category: 'parts',
      description: 'Becuri far și semnalizare',
      amount: '85 RON',
      hasReceipt: true
    },
    {
      id: 5,
      date: '15.04.2023',
      category: 'fuel',
      description: 'Plinul la Petrom',
      amount: '330 RON',
      hasReceipt: false
    }
  ]);
  
  // Categories for selection
  const categories = [
    { id: 'fuel', name: 'Combustibil', color: 'bg-purple-500', textColor: 'text-purple-500', icon: TruckIcon },
    { id: 'service', name: 'Service', color: 'bg-purple-500', textColor: 'text-purple-500', icon: ArrowPathIcon },
    { id: 'parts', name: 'Piese', color: 'bg-purple-500', textColor: 'text-purple-500', icon: FolderIcon },
    { id: 'insurance', name: 'Asigurare', color: 'bg-purple-500', textColor: 'text-purple-500', icon: ReceiptPercentIcon },
    { id: 'tax', name: 'Taxe', color: 'bg-purple-500', textColor: 'text-purple-500', icon: CurrencyEuroIcon },
    { id: 'other', name: 'Altele', color: 'bg-purple-500', textColor: 'text-purple-500', icon: FolderIcon }
  ];
  
  // Calculate total expenses
  const totalExpenses = useMemo(() => {
    return expenses.reduce((total, item) => {
      // Extract numeric value from amount string
      const amountValue = parseInt(item.amount.split(' ')[0]);
      return total + amountValue;
    }, 0);
  }, [expenses]);
  
  // Handle image upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReceiptImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Reset form
  const resetForm = () => {
    setAmount('');
    setDescription('');
    setCategory('fuel');
    setDate(new Date()); // Reset to current date as Date object
    setReceiptImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  
  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!amount || !description || !category || !date) {
      alert('Vă rugăm să completați toate câmpurile obligatorii.');
      return;
    }
    
    // Create new expense
    const newExpense = {
      id: Date.now(),
      date: date.toLocaleDateString('ro-RO'), // Format the Date object
      category,
      description,
      amount: `${amount} RON`,
      hasReceipt: !!receiptImage
    };
    
    // Add to expenses list
    setExpenses([newExpense, ...expenses]);
    
    // Reset form after submission
    resetForm();
    
    // Show success message
    alert('Cheltuială adăugată cu succes!');
  };
  
  // Get category details by ID
  const getCategoryById = (categoryId) => {
    return categories.find(cat => cat.id === categoryId) || categories[5]; // Default to "Other"
  };
  
  // Return null if modal not open
  if (!isOpen || !selectedCar) return null;
  
  const DarkModeDatePicker = ({ value, onChange, minDate, disabled }) => {
    const isDarkMode = document.documentElement.classList.contains('dark');
    
    return (
      <DatePicker
        value={value}
        onChange={onChange}
        minDate={minDate}
        disabled={disabled}
        format="dd/MM/yyyy"
        slotProps={{
          textField: {
            fullWidth: true,
            size: "small",
            variant: "outlined",
            sx: {
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDarkMode ? 'rgb(51, 65, 85)' : '#ffffff', // slate-700 in dark mode
                color: isDarkMode ? '#ffffff' : 'rgb(15, 23, 42)', // white : slate-900
                borderRadius: '0.5rem',
                '& fieldset': {
                  borderColor: isDarkMode ? 'rgb(71, 85, 105)' : 'rgb(203, 213, 225)', // slate-600 : slate-300
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? 'rgb(147, 51, 234)' : 'rgb(147, 51, 234)', // purple-600
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgb(147, 51, 234)', // purple-600
                  borderWidth: '2px',
                },
              },
              '& .MuiInputLabel-root': {
                color: isDarkMode ? 'rgb(148, 163, 184)' : 'rgb(100, 116, 139)', // slate-400 : slate-500
              },
              '& .MuiIconButton-root': {
                color: isDarkMode ? 'rgb(168, 85, 247)' : 'rgb(147, 51, 234)', // purple-400 : purple-600
              },
            }
          },
          popper: {
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: isDarkMode ? 'rgb(51, 65, 85)' : '#ffffff',
                color: isDarkMode ? '#ffffff' : 'rgb(15, 23, 42)',
                borderRadius: '0.75rem',
                boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
              },
              '& .MuiPickersDay-root': {
                color: isDarkMode ? '#ffffff' : 'rgb(15, 23, 42)',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgb(88, 28, 135)' : 'rgb(243, 232, 255)', // purple-900/30 : purple-100
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgb(147, 51, 234) !important', // purple-600
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: 'rgb(126, 34, 206) !important', // purple-700
                  },
                },
              },
              '& .MuiPickersCalendarHeader-root': {
                color: isDarkMode ? '#ffffff' : 'rgb(15, 23, 42)',
              },
              '& .MuiPickersArrowSwitcher-button': {
                color: isDarkMode ? 'rgb(168, 85, 247)' : 'rgb(147, 51, 234)',
              },
              '& .MuiDayCalendar-weekDayLabel': {
                color: isDarkMode ? 'rgb(148, 163, 184)' : 'rgb(100, 116, 139)',
              },
            }
          }
        }}
      />
    );
  };

  return (
    <div className="fixed inset-0 z-20 bg-slate-100/10 dark:bg-slate-900/10 backdrop-blur-sm flex justify-center items-center p-2">
      <div className="relative w-full max-w-5xl my-8 mb-20 md:max-h-[calc(80vh-2rem)] flex flex-col rounded-xl bg-white dark:bg-slate-800 shadow-2xl border dark:border-slate-700">
        {/* Header */}
        <div className="px-4 sm:px-4 py-2 sm:py-2 border-b border-slate-200 dark:border-slate-700 rounded-t-xl bg-purple-50 dark:bg-purple-900/20 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <div className="p-1.5 sm:p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex-shrink-0">
                <CurrencyEuroIcon 
                className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white truncate">
                  Cheltuieli
                </h2>
                {/* <p className="text-sm text-slate-600 block md:hidden dark:text-slate-400">
                  Adaugă și vizualizează cheltuieli pentru {selectedCar.plate}
                </p> */}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Mobile Tab Slider - Only visible on mobile */}
        <div className="lg:hidden px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <div className="flex space-x-2 bg-white dark:bg-slate-700 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('add')}
              className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                activeTab === 'add'
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <PencilSquareIcon className="w-4 h-4 inline-block mr-2" />
              Adaugă
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                activeTab === 'history'
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <ListBulletIcon className="w-4 h-4 inline-block mr-2" />
              Istoric cheltuieli
            </button>
          </div>
        </div>
        
        <div className=" flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
            {/* Add new expense form */}
            <div className={`lg:col-span-2 bg-white dark:bg-slate-800 shadow-sm ${
              activeTab === 'history' ? 'hidden lg:block' : ''
            }`}>
              <div className="hidden lg:block px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <PencilSquareIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Adaugă cheltuială</h3>           
                </div>
                </div> 
              </div>
              
              <form onSubmit={handleSubmit} className="p-4 space-y-1">
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Categorie</label>
                  <select
                    id="category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-slate-700 dark:text-white sm:text-sm"
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Descriere</label>
                  <input
                    type="text"
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-slate-700 dark:text-white sm:text-sm"
                    placeholder="Ex: Schimb ulei, Plin combustibil"
                  />
                </div>
                
                <div>
                  <label htmlFor="amount" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Suma (RON)</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-slate-500 dark:text-slate-400 sm:text-sm">RON</span>
                    </div>
                    <input
                      type="number"
                      id="amount"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      pattern="^\d+(\.\d{1,2})?$"
                      value={amount}
                      onChange={(e) => {
                        // Only allow numbers and decimal point
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setAmount(value);
                      }}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-slate-700 dark:text-white sm:text-sm"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={roLocale}>
                  <label htmlFor="date" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Data</label>
                  <div className="relative">
                    <DarkModeDatePicker
                      value={date}
                      onChange={(newDate) => setDate(newDate)}
                    />
                  </div>
            </LocalizationProvider>
                
                <div className="pt-2 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="inline-flex items-center px-3 py-2 border border-slate-300 dark:border-slate-600 shadow-sm text-sm font-medium rounded-lg text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600"
                  >
                    Resetează
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-gray-800"
                  >
                    <PlusCircleIcon className="h-4 w-4 mr-2" />
                    Adaugă
                  </button>
                </div>
              </form>
            </div>
            
            {/* List of expenses */}
            <div className={`lg:col-span-3 bg-white dark:bg-slate-800 shadow-sm flex flex-col overflow-y-auto ${
              activeTab === 'add' ? 'hidden lg:flex' : ''
            }`}>
              <div className="hidden lg:block px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 flex-shrink-0">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <ListBulletIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Cheltuieli recente</h3>
                  </div>
                </div>
              </div>
              
              {expenses.length > 0 ? (
                <div className="p-4 space-y-3">
                  {expenses.map((expense) => {
                    const expenseCategory = getCategoryById(expense.category);
                    return (
                      <div 
                        key={expense.id} 
                        className="bg-white dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600 p-3 sm:p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3">
                            <div className={`${expenseCategory.color} p-2 rounded-full flex-shrink-0`}>
                              <expenseCategory.icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                            </div>
                            <div>
                              <h4 className="text-sm sm:text-base font-medium text-slate-900 dark:text-white mb-0.5">{expense.description}</h4>
                              <div className="flex items-center text-xs text-slate-500 dark:text-slate-400 space-x-2">
                                <span>{expenseCategory.name}</span>
                                <span>•</span>
                                <span className="flex items-center">
                                  <CalendarIcon className="h-3 w-3 mr-1" />
                                  {expense.date}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="text-sm sm:text-base font-semibold text-slate-900 dark:text-white">
                              {expense.amount}
                            </span>
                            {expense.hasReceipt && (
                              <span className="text-xs text-purple-600 dark:text-purple-400 flex items-center mt-1">
                                <PhotoIcon className="h-3 w-3 mr-1" />
                                Bon
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 px-4">
                  <CurrencyEuroIcon className="h-12 w-12 mx-auto text-slate-400 dark:text-slate-500 mb-4" />
                  <p className="text-slate-500 dark:text-slate-400">
                    Nu există cheltuieli înregistrate.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Total expenses section */}
        {expenses.length > 0 && (
          <div className="px-6 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                  Total:
                </span>
                <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                  {totalExpenses} RON
                </span>
              </div>
            </div>
          </div>
        )}
        
        {/* Footer/Close button */}
        <div className="px-3 sm:px-6 py-3 sm:py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-end rounded-b-xl flex-shrink-0">
          <button
            type="button"
            className="w-full sm:w-auto bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-300 dark:border-slate-600 font-medium py-2 px-4 rounded-lg transition-colors"
            onClick={onClose}
          >
            Închide
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpensesModal;
